import type { FC } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { ListItem } from '@tiptap/extension-list-item';
import { TaskItem } from '@tiptap/extension-task-item';
import { TaskList } from '@tiptap/extension-task-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { EditorProvider } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import axios from 'axios';
import { debounce, has } from 'lodash';
import toast from 'react-hot-toast';

import type { Meeting } from '@/types/meeting';
import { config } from '@/config';

import MeetingTiptapContent from './MeetingTiptapContent';
import MeetingTiptapMenu from './MeetingTiptapMenu';

import '../styles/meeting-tiptap.css';

interface MeetingTiptapProps {
  meeting: Meeting | null;
}
const MeetingTiptap: FC<MeetingTiptapProps> = ({ meeting }) => {
  const extensions = useMemo(
    () => [
      Color.configure({ types: [TextStyle.name, ListItem.name] }),
      StarterKit.configure({
        bulletList: {
          keepMarks: false,
        },
        orderedList: {
          keepMarks: false,
        },
        heading: {
          levels: [1, 2, 3],
        },
        paragraph: {},
        codeBlock: {},
        blockquote: {},
        horizontalRule: {},
      }),
      Highlight.configure({}),
      TextAlign.configure({
        types: ['heading', 'paragraph', 'code', 'blockquote'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      TaskList.configure({}),
      TaskItem.configure({}),
    ],
    [meeting?.id]
  );

  const handleUpdateMeetingContent = useCallback(
    async (content: string) => {
      if (!meeting?.id) return;

      try {
        axios.defaults.withCredentials = true;
        await axios.get(`${config.site.api}/sanctum/csrf-cookie`);
        const response = await axios.patch(
          `${config.site.api}/api/v1/meetings/${meeting.id}`,
          { content },
          {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
          }
        );

        const data = response.data;
        if (has(data, 'error')) {
          toast.error(data.error?.message);
        }
      } catch (error) {
        toast.error(error?.message);
        console.error(error?.message);
      }
    },
    [meeting?.id]
  );
  const updateContent = debounce(handleUpdateMeetingContent, 1000);

  if (!meeting) {
    return null;
  }

  return (
    <EditorProvider
      content={meeting.content ?? ''}
      editorProps={{}}
      extensions={extensions}
      onUpdate={({ editor }) => {
        updateContent(editor.getHTML());
      }}
      slotBefore={<MeetingTiptapMenu />}
    >
      <MeetingTiptapContent />
    </EditorProvider>
  );
};

export default MeetingTiptap;
